import { Box, IconButton, Typography } from "@mui/material";
import html2pdf from "html2pdf.js";
import { useCallback, useRef, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import useColors from "../../../api/hooks/useColors";
import CustomTooltip from "../../../components/CustomTooltip";
import LoadingIcon from "../../../components/LoadingIcon";
import AppPage from "../../../components/structure/AppPage";
import { Toast } from "../../../components/Toast";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../hooks/useFileNaming";
import SvgToIcon from "../../../icons/SvgToIcon";
import { icons } from "../../stack/environment/icons/iconExport";
import ControlDrawerWrapper from "../ControlDrawerWrapper";
import {
  createPdfConfig,
  createPdfContainer,
  measureContentForPdf,
} from "../custom_dashboard/utils/commonWidgetUtils";
import { GRID_CONFIG } from "../custom_dashboard/widgetDefinitions";
import WidgetWrapper from "../custom_dashboard/WidgetWrapper";
import { useAIDashboard } from "./hooks/useAIDashboard";
import AIAppUsageWidget from "./widgets/ai_app_usage/AIAppUsageWidget";
import AIIntegrationsWidget from "./widgets/ai_integrations/AIIntegrationsWidget";
import NHITableWidget from "./widgets/nhi_table/NHITableWidget";
import NHITypesDistribution from "./widgets/nhi_types_distribution/NHITypesDistribution";
import TopMCPServersWidget from "./widgets/top_mcp_servers/TopMCPServersWidget";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const WidgetContent = ({ widget, widgetRef, registerWidgetExport }) => {
  if (widget.contentType === "text") {
    return (
      <WidgetWrapper title={widget.title}>
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">{widget.description}</Typography>
        </Box>
      </WidgetWrapper>
    );
  }

  if (
    widget.contentType === "component" &&
    widget.componentType === "AIIntegrationsWidget"
  ) {
    return (
      <AIIntegrationsWidget
        ref={widgetRef}
        registerWidgetExport={registerWidgetExport}
        widgetId={widget.id}
      />
    );
  }

  if (
    widget.contentType === "component" &&
    widget.componentType === "NHITypesDistribution"
  ) {
    return (
      <NHITypesDistribution
        ref={widgetRef}
        registerWidgetExport={registerWidgetExport}
        widgetId={widget.id}
      />
    );
  }

  if (
    widget.contentType === "component" &&
    widget.componentType === "NHITableWidget"
  ) {
    return <NHITableWidget />;
  }

  if (
    widget.contentType === "component" &&
    widget.componentType === "TopMCPServersWidget"
  ) {
    return (
      <TopMCPServersWidget
        ref={widgetRef}
        registerWidgetExport={registerWidgetExport}
        widgetId={widget.id}
      />
    );
  }

  if (
    widget.contentType === "component" &&
    widget.componentType === "AIAppUsageWidget"
  ) {
    return <AIAppUsageWidget />;
  }

  return (
    <WidgetWrapper title={widget.title}>
      <Box sx={{ p: 2 }}>
        <Typography>Widget type not supported yet</Typography>
      </Box>
    </WidgetWrapper>
  );
};

const AIDashboard = () => {
  const colors = useColors();
  const {
    widgets,
    generateLayouts,
    isLoading,
    isError,
    onLayoutChange,
    mounted,
  } = useAIDashboard();
  const widgetRefs = useRef({});
  const [exportingWidgets, setExportingWidgets] = useState({});
  const [exportFunctions, setExportFunctions] = useState({});

  const registerWidgetExport = useCallback((widgetId, exportFn) => {
    setExportFunctions((prev) => ({
      ...prev,
      [widgetId]: exportFn,
    }));
  }, []);

  const handleExportAllToPdf = async () => {
    const exportFunctionEntries = Object.entries(exportFunctions);

    if (exportFunctionEntries.length === 0) {
      Toast.error("No widgets available for PDF export");
      return;
    }

    setExportingWidgets(
      Object.keys(exportFunctions).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      )
    );

    const entirePageElement = await createPdfContainer();

    for (const [widgetId, exportFunction] of exportFunctionEntries) {
      const widgetContent = await exportFunction(true);

      if (widgetContent) {
        const spacer = document.createElement("div");
        spacer.style.cssText = "height: 20px; width: 100%;";
        entirePageElement.appendChild(spacer);

        entirePageElement.appendChild(widgetContent);
      }
    }

    const { contentWidth, contentHeight } = await measureContentForPdf(
      entirePageElement
    );

    const pdfConfig = createPdfConfig(
      generateFileName(
        "Breeze_AI_Posture_Dashboard",
        null,
        FILE_EXTENSIONS.PDF
      ),
      contentWidth,
      contentHeight
    );

    await html2pdf().set(pdfConfig).from(entirePageElement).save();

    Toast.success("AI Posture dashboard PDF exported successfully");

    setExportingWidgets({});
  };

  const exportCount = Object.keys(exportFunctions).length;

  const styles = {
    dashboard: {
      fontFamily: "Poppins",
    },
    dashboardHeader: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "20px 15px",
      marginTop: "-50px",
    },
    dashboardContainer: {
      minHeight: "600px",
      backgroundColor: "none",
      borderRadius: "12px",
      padding: "10px",
    },
    widget: {
      backgroundColor: "white",
      boxShadow: `0px 0px 2px 1px ${colors.grey_24percent}`,
      borderRadius: "12px",
      overflow: "hidden",
      height: "100%",
    },
  };

  const dragStyles = `
    .react-grid-item.react-grid-placeholder {
      background: ${colors.primary_50} !important;
      border: 1px dashed ${colors.primary_dark} !important;
      border-radius: 12px !important;
    }
    
    .react-grid-item.react-draggable-dragging {
      border: 1px solid ${colors.primary_8percent} !important;
      border-radius: 12px !important;
    }
    .react-grid-layout.layout {
      background: ${colors.common_white} !important;
    }
  `;

  if (isLoading) {
    return (
      <Box style={styles.dashboard}>
        <LoadingIcon />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box style={styles.dashboard}>
        <Typography color="error">
          Error loading AI Posture dashboard. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <AppPage title="AI Posture">
      <Box>
        <style>{dragStyles}</style>
        <Box style={styles.dashboard}>
          {/* Export All Widgets Button */}
          {exportCount > 0 && (
            <Box
              sx={{
                padding: "15px",
                marginTop: "-50px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexDirection: "row",
                justifyContent: "flex-end",
              }}
            >
              <CustomTooltip title="Export all widgets to PDF" placement="top">
                <IconButton
                  onClick={handleExportAllToPdf}
                  disabled={Object.values(exportingWidgets).some((v) => v)}
                  sx={{
                    background: colors.common_white,
                    color: colors.action_active,
                    height: "32px",
                    width: "32px",
                    border: "1px solid " + colors.enabled_stroke,
                    borderRadius: "8px",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      background: colors.grey_8percent,
                    },
                  }}
                >
                  <SvgToIcon
                    IconLogo={icons.ExportPDFIcon}
                    style={{ width: "18px" }}
                  />
                </IconButton>
              </CustomTooltip>
            </Box>
          )}

          {/* Dashboard Grid */}
          <Box style={styles.dashboardContainer}>
            <ResponsiveReactGridLayout
              className="layout"
              layouts={generateLayouts}
              breakpoints={GRID_CONFIG.breakpoints}
              cols={GRID_CONFIG.cols}
              rowHeight={GRID_CONFIG.rowHeight}
              onLayoutChange={onLayoutChange}
              isDraggable={true}
              isResizable={false}
              measureBeforeMount={false}
              useCSSTransforms={mounted}
              style={{ background: colors.grey_100, borderRadius: "12px" }}
              compactType="vertical"
              preventCollision={false}
              margin={GRID_CONFIG.margin}
              containerPadding={GRID_CONFIG.containerPadding}
              draggableHandle={GRID_CONFIG.draggableHandle}
              resizeHandles={GRID_CONFIG.resizeHandles}
            >
              {widgets.map((widget) => {
                if (!widgetRefs.current[widget.id]) {
                  widgetRefs.current[widget.id] = { current: null };
                }
                return (
                  <div key={widget.id}>
                    <Box style={{ ...styles.widget, height: "100%" }}>
                      <WidgetContent
                        widget={widget}
                        widgetRef={widgetRefs.current[widget.id]}
                        registerWidgetExport={registerWidgetExport}
                      />
                    </Box>
                  </div>
                );
              })}
            </ResponsiveReactGridLayout>
          </Box>
          <ControlDrawerWrapper />
        </Box>
      </Box>
    </AppPage>
  );
};

export default AIDashboard;
